import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import TableContainer from "../../Components/Common/TableContainer";
import * as Yup from "yup";
import {
  Button,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  Label,
  Card,
  CardBody,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import Spinners from "Components/Common/Spinner";
import { useFormik } from "formik";

//import components
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import EcommerceOrdersModal from "./EcommerceOrdersModal";

import {
  useDeleteOrder,
  useGetOrders,
  useUpdateOrderStatus,
} from "hooks/useOrders";
import { getStatusColor, readableDate } from "utils";
import { useDispatch, useSelector } from "react-redux";

import { setOrderId } from "slices/cart/reducer";
import { RootState } from "slices";
import { capitalize } from "lodash";

function Orders() {
  //meta title
  document.title = "Papermap | Orders";
  const navigate = useNavigate();

  const selectedBranch = useSelector(
    (state: RootState) => state.branch?.currentBranch
  );

  const { data, isLoading } = useGetOrders(selectedBranch?.id);
  const { mutate: deleteOrder } = useDeleteOrder();
  const { mutate: updateOrderStatus } = useUpdateOrderStatus();
  const [editDetails, setEditDetails] = useState<any>("");
  const [modal, setModal] = useState<boolean>(false);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [order, setOrder] = useState<any>(null);

  const dispatch = useDispatch();

  // validation
  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: (order && order.status) || "",
    },
    validationSchema: Yup.object({
      status: Yup.string().required("Please Enter Order Status"),
    }),
    onSubmit: (values) => {
      const payload: any = {
        id: editDetails.id,
        status: values.status,
      };

      updateOrderStatus(
        { id: order.id, data: payload },
        {
          onSuccess: (response) => {
            toggle();
          },
        }
      );
    },
  });

  const toggleViewModal = useCallback(
    (data: any) => {
      setViewModal(!viewModal);
      setEditDetails(data);
    },
    [viewModal]
  );

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setOrder(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const handleOrderClick = useCallback(
    (arg: any) => {
      console.log("Incoming order data:", arg); // Debug log

      const order = arg;
      setOrder({
        ...order, // Preserve all original fields

        status: order.status,
      });
      toggle();
    },
    [toggle]
  );

  const handleOrderEditClick = async (orderId: string | number) => {
    await dispatch(setOrderId(orderId.toString()));
    console.log("Set Order id: ", orderId);
    navigate(`/pos`);
  };

  //delete order
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const onClickDelete = (order: any) => {
    setOrder(order);
    setDeleteModal(true);
  };

  const handleDeleteOrder = () => {
    if (order.id) {
      deleteOrder(order.id);
      setDeleteModal(false);
    }
  };

  const handleOrderClicks = () => {
    setOrder("");
    toggle();
  };

  const columns = useMemo(
    () => [
      {
        header: "Order ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => {
          return (
            <Link to="#" className="text-body fw-bold">
              {cellProps.row.original.id}
            </Link>
          );
        },
      },
      {
        header: "Date",
        accessorKey: "order_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => {
          return `${readableDate(cellProps.row.original.order_date)}`;
        },
      },
      {
        header: "Amount",
        accessorKey: "total_amount",
        enableColumnFilter: false,
        cell: (cellProps: any) => (
          <span>
            GHS {Number(cellProps.row.original.total_amount).toFixed(2)}
          </span>
        ),
      },

      {
        header: "Payment Method",

        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => {
          const paymentMethod = cellProps.row.original.payment_method || "N/A";

          const getPaymentIcon = (method: string) => {
            switch (method) {
              case "bank":
                return "mdi-bank-transfer";
              case "momo":
                return "fas fa-mobile-alt";
              case "cash":
                return "fas fa-money-bill-wave";
              default:
                return "mdi mdi-help-circle";
            }
          };

          return (
            <div className="font-size-12">
              <i className={`${getPaymentIcon(paymentMethod.type)} me-1`}></i>
              {paymentMethod.name === "card"
                ? "Credit/Debit Card"
                : paymentMethod.name === "crypto"
                ? "Cryptocurrency"
                : paymentMethod.name === "paypal"
                ? "PayPal"
                : paymentMethod.name}
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => {
          const status = cellProps.row.original.status;

          return (
            <Badge
              className={`font-size-12 badge-soft-${getStatusColor(status)}`}
            >
              <i
                className="mdi mdi-circle me-1"
                style={{ fontSize: "10px" }}
              ></i>
              {capitalize(status)}
            </Badge>
          );
        },
      },
      {
        header: "Update Status",

        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <Button
            type="button"
            color="soft-info"
            className="btn-sm btn-rounded "
            onClick={() => handleOrderClick(cellProps.cell.row.original)}
          >
            Update
          </Button>
        ),
      },

      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => {
          return (
            <>
              <ul className="list-unstyled hstack gap-1 mb-0">
                <li id="viewtooltip">
                  <Link
                    to={""}
                    className="btn btn-sm btn-soft-primary"
                    onClick={() => toggleViewModal(cellProps.row.original)}
                  >
                    <i className="mdi mdi-eye-outline"></i>
                    <UncontrolledTooltip placement="top" target="viewtooltip">
                      View
                    </UncontrolledTooltip>
                  </Link>
                </li>
                {cellProps.row.original.status === "draft" && (
                  <li id="edittooltip">
                    <Link
                      className="btn btn-sm btn-soft-info"
                      to="#"
                      onClick={() => {
                        handleOrderEditClick(cellProps.row.original.id);
                      }}
                    >
                      <i className="mdi mdi-pencil-outline" />
                      <UncontrolledTooltip placement="top" target="edittooltip">
                        Edit
                      </UncontrolledTooltip>
                    </Link>
                  </li>
                )}
                {cellProps.row.original.status === "success" ? null : (
                  <li id="deleteTooltip">
                    <Link
                      to="#"
                      className="btn btn-sm btn-soft-danger"
                      onClick={() => onClickDelete(cellProps.row.original)}
                    >
                      <i className="mdi mdi-delete" />
                      <UncontrolledTooltip
                        placement="top"
                        target="deleteTooltip"
                      >
                        Delete
                      </UncontrolledTooltip>
                    </Link>
                  </li>
                )}
              </ul>
            </>
          );
        },
      },
    ],
    [handleOrderClick, toggleViewModal]
  );

  return (
    <React.Fragment>
      <EcommerceOrdersModal
        isOpen={viewModal}
        toggle={toggleViewModal}
        editDetails={editDetails}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteOrder}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Ecommerce" breadcrumbItem="Orders" />
          {isLoading ? (
            <Spinners />
          ) : (
            <Row>
              <Col xs={12}>
                <Card>
                  <CardBody>
                    <TableContainer
                      columns={columns}
                      data={data || []}
                      isGlobalFilter={true}
                      isAddButton={false}
                      isCustomPageSize={true}
                      handleUserClick={handleOrderClicks}
                      isPagination={true}
                      SearchPlaceholder="Search order."
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              Update Order Status
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <Row>
                  <Col xs={12}>
                    <div className="mb-3">
                      <Label>Status</Label>
                      <Input
                        name="status"
                        type="select"
                        className="form-select"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.status}
                      >
                        {validation?.value?.status !== "approved" && (
                          <option value="draft">Draft</option>
                        )}
                        <option value="in review">In Review</option>
                        {validation?.value?.status !== "draft" && (
                          <option value="approved">Approved</option>
                        )}
                      </Input>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="text-end">
                      <Button type="submit" color="success">
                        Update
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  );
}
export default Orders;
